import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import host from "../../global";
import { TIMING } from "../../constants/interviewInstructions";
import {
  PHASES,
  SETUP_STEPS,
  SESSION_STATUS,
  FACING_MODE,
  CAPTURE_WIDTH,
  CAPTURE_HEIGHT,
  CAPTURE_JPEG_QUALITY,
  VIDEO_READY_TIMEOUT_MS,
  PAIRED_STORAGE_KEY_PREFIX,
  UPLOAD_FAILURES_BEFORE_RECOVERY,
  UPLOAD_RECOVERY_INTERVAL_MS,
  PORTRAIT_RETRY_INTERVAL_MS,
} from "../../constants/secondaryCamera";
import useWakeLock from "../../hooks/useWakeLock";
import useOrientation from "../../hooks/useOrientation";
import ValidatingScreen from "./screens/ValidatingScreen";
import EndedScreen from "./screens/EndedScreen";
import AccessDeniedScreen from "./screens/AccessDeniedScreen";
import InstructionSlide from "./screens/setup/InstructionSlide";
import PositioningSlide from "./screens/setup/PositioningSlide";
import ActiveScreen from "./screens/ActiveScreen";

// Phases: validating → invalid | setup → positioning → active → (ended | rescan)
const SecondaryCamera = () => {
  const { token } = useParams();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const needsPairRef = useRef(true);
  const cameraLostHandlerRef = useRef(null);

  const [phase, setPhase] = useState(PHASES.VALIDATING);
  const [monitoring, setMonitoring] = useState(false);
  const [starting, setStarting] = useState(false);
  const [interviewData, setInterviewData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState("");
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [facingMode, setFacingMode] = useState(FACING_MODE.ENVIRONMENT);
  const [setupStepIndex, setSetupStepIndex] = useState(0);
  const [cameraArmed, setCameraArmed] = useState(false);
  const [positioningBackEnabled, setPositioningBackEnabled] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  // Which "can't proceed" copy to show: "expired" (no live pairing, needs a
  // fresh QR) vs "claimed" (another device holds it and may be monitoring fine).
  const [rescanVariant, setRescanVariant] = useState("expired");
  const stoppedRef = useRef(false);
  const uploadFailCountRef = useRef(0);
  const portraitSkipRef = useRef(false);

  // Also held during POSITIONING — otherwise the screen could sleep mid-setup.
  useWakeLock(monitoring || phase === PHASES.POSITIONING);

  // Gates "Start Monitoring" and shows the rotate prompt.
  const isLandscape = useOrientation();

  // Device key minted at pairing, presented on reload to resume it (the token
  // alone can't). localStorage so it survives closing/reopening the tab.
  const pairedKey = `${PAIRED_STORAGE_KEY_PREFIX}${token}`;
  const getStoredDeviceKey = () => localStorage.getItem(pairedKey) || null;
  const setStoredDeviceKey = (key) => localStorage.setItem(pairedKey, key);
  const clearStoredDeviceKey = () => localStorage.removeItem(pairedKey);

  useEffect(() => {
    validateToken();
    return () => stopMonitoring();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const handleBeforeUnload = () => stopMonitoring();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === TOKEN VALIDATION ===
  const validateToken = async () => {
    try {
      const deviceKey = getStoredDeviceKey();
      const { data } = await axios.get(`${host}/secondary-camera/sessions/${token}`, {
        params: deviceKey ? { device_key: deviceKey } : {},
      });
      setInterviewData(data);

      if (data.status === SESSION_STATUS.CLOSED) {
        // completed → this round finished; otherwise the QR was superseded/expired.
        if (data.completed) {
          setPhase(PHASES.ENDED);
        } else {
          setRescanVariant("expired");
          setPhase(PHASES.RESCAN);
        }
        return;
      }
      if (data.status === SESSION_STATUS.PAIRED) {
        // resume only when the server confirms this device holds the pairing;
        // otherwise it was claimed elsewhere — that device may be monitoring fine.
        if (data.resume) {
          needsPairRef.current = false;
          setCameraArmed(false);
          setPositioningBackEnabled(false);
          setPhase(PHASES.POSITIONING);
        } else {
          setRescanVariant("claimed");
          setPhase(PHASES.RESCAN);
        }
        return;
      }
      // pending
      needsPairRef.current = true;
      setSetupStepIndex(0);
      setPhase(PHASES.SETUP);
    } catch {
      setPhase(PHASES.INVALID);
    }
  };

  // === CAMERA ===
  // facingMode is tracked in state, not read back from the stream: getSettings()
  // isn't reliable on every device, which would silently break the rotate toggle.
  const acquireCamera = async (desiredFacing = FACING_MODE.ENVIRONMENT) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Camera not supported on this device or browser");
    }
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: desiredFacing },
          width: { ideal: CAPTURE_WIDTH },
          height: { ideal: CAPTURE_HEIGHT },
        },
      });
    } catch (err) {
      if (err.name === "OverconstrainedError") {
        return await navigator.mediaDevices.getUserMedia({ video: true });
      }
      throw err;
    }
  };

  const attachStream = (stream) => {
    streamRef.current = stream;
    const el = videoRef.current;
    if (el && el.srcObject !== stream) {
      el.srcObject = stream;
      el.play().catch(() => {});
    }
    // A dead track leaves <video> frozen on its last frame, so uploads would keep
    // "succeeding" with a stale image and defeat gap-analysis disconnect detection.
    const track = stream.getVideoTracks()[0];
    if (track) {
      cameraLostHandlerRef.current = () => {
        stopMonitoring();
        setCameraArmed(false);
        setPositioningBackEnabled(false);
        setPhase(PHASES.POSITIONING);
        setError("Camera connection was lost. Reconnecting…");
      };
      track.addEventListener("ended", cameraLostHandlerRef.current, { once: true });
    }
  };

  // Stream may be acquired before its <video> exists — reattaches once it mounts.
  const videoRefCallback = useCallback((el) => {
    videoRef.current = el;
    if (el && streamRef.current && el.srcObject !== streamRef.current) {
      el.srcObject = streamRef.current;
      el.play().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve once the video has real dimensions, so the first frame isn't blank.
  const waitForVideoReady = (video, timeoutMs = VIDEO_READY_TIMEOUT_MS) =>
    new Promise((resolve) => {
      if (!video || video.videoWidth > 0) return resolve();
      const finish = () => {
        video.removeEventListener("loadedmetadata", finish);
        video.removeEventListener("canplay", finish);
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(finish, timeoutMs);
      video.addEventListener("loadedmetadata", finish);
      video.addEventListener("canplay", finish);
    });

  // === SETUP WIZARD (instruction slides) ===
  // Both clear stale camera error/denied state so it can't leak onto an earlier slide.
  const goToNextSetupStep = () => {
    setError("");
    setPermissionDenied(false);
    setSetupStepIndex((i) => Math.min(SETUP_STEPS.length - 1, i + 1));
  };
  const goToPrevSetupStep = () => {
    setError("");
    setPermissionDenied(false);
    setSetupStepIndex((i) => Math.max(0, i - 1));
  };

  // Shared by the gesture-initiated call below and the no-gesture auto-attempt.
  const tryAcquireAndAttach = async () => {
    try {
      const stream = await acquireCamera(FACING_MODE.ENVIRONMENT);
      attachStream(stream);
      setFacingMode(FACING_MODE.ENVIRONMENT);
      return true;
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setPermissionDenied(true);
        setError("Camera permission was denied. Allow camera access and try again.");
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else {
        setError(`Camera access failed: ${err.message || "Unknown error"}.`);
      }
      return false;
    }
  };

  // Used by the auto-attempt effect below, and by the manual retry tile.
  const armPositioningCamera = async () => {
    if (starting) return false;
    setStarting(true);
    setError("");
    setPermissionDenied(false);
    const ok = await tryAcquireAndAttach();
    setStarting(false);
    if (ok) setCameraArmed(true);
    return ok;
  };

  // Called synchronously from Continue — must stay gesture-initiated.
  const acquireCameraAndEnterPositioning = async () => {
    if (!(await armPositioningCamera())) return; // stays on the slide, error/denied state shown
    setPositioningBackEnabled(true);
    setPhase(PHASES.POSITIONING);
  };

  // === MONITORING LIFECYCLE ===
  // "Start Monitoring" — pair if needed, then go active; camera is already attached.
  const confirmPositioning = async () => {
    if (starting || monitoring) return; // re-entrancy guard: a double-tap must not race two starts
    setStarting(true);
    setError("");

    if (needsPairRef.current) {
      try {
        const { data } = await axios.post(`${host}/secondary-camera/sessions/${token}/pair`);
        if (!data.paired) {
          stopCameraOnly();
          // "completed" means the round finished while this candidate was still on
          // the positioning slide — that's a success, not a dead end.
          if (data.reason === "completed") {
            setPhase(PHASES.ENDED);
          } else {
            setRescanVariant(data.reason === "already_claimed" ? "claimed" : "expired");
            setPhase(PHASES.RESCAN);
          }
          setStarting(false);
          return;
        }
        setStoredDeviceKey(data.device_key);
        // Reset so later recovery re-entries don't re-POST /pair — the backend's claim only matches once.
        needsPairRef.current = false;
      } catch {
        // Camera's still fine and the pairing hasn't been consumed — a network blip
        // shouldn't tear down the camera; let them retry "Start Monitoring" right here.
        setError("Couldn't reach the server. Check your connection and try again.");
        setStarting(false);
        return;
      }
    }

    // Switch to "active" immediately so the button doesn't freeze while the video
    // warms up — the capture loop starts below once <video> has actually mounted.
    stoppedRef.current = false;
    setMonitoring(true);
    setStarting(false);
    setPhase(PHASES.ACTIVE);
  };

  const backFromPositioning = () => {
    stopCameraOnly();
    setCameraArmed(false);
    setPositioningBackEnabled(false);
    setError("");
    setPermissionDenied(false);
    setPhase(PHASES.SETUP); // setupStepIndex is left as-is, still the last step
  };

  // No gesture available on resume/recovery entry, so auto-attempt once here.
  useEffect(() => {
    if (phase !== PHASES.POSITIONING || cameraArmed || starting) return;
    armPositioningCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, cameraArmed]);

  // Self-scheduling (not setInterval): delay tightens during a self-heal retry,
  // and a slow upload can never overlap the next tick. A skipped portrait frame
  // retries fast — waiting a full interval would trip the 4-min live gate.
  const scheduleNextCapture = () => {
    if (stoppedRef.current) return;
    const delay = portraitSkipRef.current
      ? PORTRAIT_RETRY_INTERVAL_MS
      : uploadFailCountRef.current >= UPLOAD_FAILURES_BEFORE_RECOVERY
        ? UPLOAD_RECOVERY_INTERVAL_MS
        : TIMING.SECONDARY_CAPTURE_INTERVAL_MS;
    intervalRef.current = setTimeout(async () => {
      await captureAndUpload();
      scheduleNextCapture();
    }, delay);
  };

  // Starts once ACTIVE's <video> has mounted — doing it inside confirmPositioning would race the commit.
  useEffect(() => {
    if (!monitoring) return;
    let cancelled = false;
    (async () => {
      await waitForVideoReady(videoRef.current);
      if (cancelled || stoppedRef.current) return;
      await captureAndUpload(); // frame #0
      // captureAndUpload may have already called stopMonitoring() — don't
      // resurrect a cleared chain.
      if (!cancelled && !stoppedRef.current) {
        scheduleNextCapture();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitoring]);

  const captureFrame = () =>
    new Promise((resolve) => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) {
        resolve(null);
        return;
      }
      // Never upload a portrait frame (screen not landscape, or not caught up yet).
      portraitSkipRef.current = video.videoWidth < video.videoHeight;
      if (portraitSkipRef.current) {
        resolve(null);
        return;
      }
      const scale = Math.min(1, CAPTURE_WIDTH / video.videoWidth);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", CAPTURE_JPEG_QUALITY);
    });

  const captureAndUpload = async () => {
    const blob = await captureFrame();
    if (!blob) return;
    // One id per capture, reused across the retry below so a network-timeout retry
    // dedups server-side instead of storing the frame twice.
    const captureId =
      crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const attempt = async () => {
      const form = new FormData();
      form.append("capture_id", captureId);
      form.append("image", blob, `sc_${captureId}.jpg`);
      const res = await axios.post(
        `${host}/secondary-camera/sessions/${token}/screenshots`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return res.data;
    };

    // 404 = session gone (rotated/closed) → terminal, rescan. Anything else is
    // transient: one retry, then drop the frame — the gap threshold tolerates it.
    let data;
    try {
      data = await attempt();
    } catch (err) {
      if (err.response?.status === 404) {
        stopMonitoring();
        setRescanVariant("expired");
        setPhase(PHASES.RESCAN);
        return;
      }
      try {
        data = await attempt();
      } catch (err2) {
        if (err2.response?.status === 404) {
          stopMonitoring();
          setRescanVariant("expired");
          setPhase(PHASES.RESCAN);
        } else {
          console.error("[SecondaryCamera] Screenshot upload failed:", err2);
          uploadFailCountRef.current += 1;
          if (uploadFailCountRef.current >= UPLOAD_FAILURES_BEFORE_RECOVERY) {
            setConnectionLost(true);
          }
        }
        return;
      }
    }

    // Any parsed response (even a rejected frame) proves connectivity — self-heal over.
    uploadFailCountRef.current = 0;
    setConnectionLost(false);

    if (data.interview_ended) {
      handleInterviewEnded();
    } else if (data.session_closed) {
      // session_closed only ever fires for a non-completed close (completion goes
      // through interview_ended above) — always the "no live pairing" bucket.
      stopMonitoring();
      setRescanVariant("expired");
      setPhase(PHASES.RESCAN);
    }
    // data.throttled / success:false → drop this frame, keep monitoring.
  };

  const handleInterviewEnded = () => {
    stopMonitoring();
    clearStoredDeviceKey();
    const id = interviewData?.interview_id;
    if (id) window.location.href = `/secondary-camera-thankyou/${id}`;
    else setPhase(PHASES.ENDED);
  };

  const stopCameraOnly = () => {
    if (streamRef.current) {
      // Detach the "ended" listener first, so this deliberate stop doesn't
      // trigger the "connection lost" recovery path for a track we killed on purpose.
      const track = streamRef.current.getVideoTracks()[0];
      if (track && cameraLostHandlerRef.current) {
        track.removeEventListener("ended", cameraLostHandlerRef.current);
      }
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const stopMonitoring = () => {
    stoppedRef.current = true;
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
    stopCameraOnly();
    setMonitoring(false);
  };

  const rotateCameraFacing = async () => {
    if (!streamRef.current) return;
    const current = facingMode;
    const next = current === FACING_MODE.ENVIRONMENT ? FACING_MODE.USER : FACING_MODE.ENVIRONMENT;
    stopCameraOnly();
    try {
      const stream = await acquireCamera(next);
      attachStream(stream);
      setFacingMode(next);
    } catch {
      // Fall back to the camera that was working before the switch attempt.
      try {
        const stream = await acquireCamera(current);
        attachStream(stream);
        setError("Couldn't switch camera. Kept the previous one.");
      } catch {
        // Neither camera reachable — route back to the live-position slide, not a dead loop.
        stopMonitoring();
        setCameraArmed(false);
        setPositioningBackEnabled(false);
        setPhase(PHASES.POSITIONING);
        setError("Camera unavailable. Tap to try again.");
      }
    }
  };

  // iPhone Safari never supports the Fullscreen API for non-video elements (iPadOS does) —
  // hide the button there instead of offering a tap that silently does nothing.
  const fullscreenSupported = !!document.fullscreenEnabled;

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // No orientation lock: a screen pinned to landscape on a phone stood
        // upright yields landscape-shaped but sideways frames.
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {
      setIsFullscreen(!!document.fullscreenElement);
    }
  };

  // === RENDER ===
  if (phase === PHASES.VALIDATING) return <ValidatingScreen />;

  if (phase === PHASES.ENDED) {
    return <EndedScreen candidateName={interviewData?.candidate_name} round={interviewData?.round} />;
  }

  if (phase === PHASES.INVALID || phase === PHASES.RESCAN) {
    return <AccessDeniedScreen variant={phase === PHASES.RESCAN ? rescanVariant : "invalid"} />;
  }

  if (phase === PHASES.SETUP) {
    const isLastStep = setupStepIndex === SETUP_STEPS.length - 1;
    return (
      <InstructionSlide
        step={SETUP_STEPS[setupStepIndex]}
        index={setupStepIndex}
        total={SETUP_STEPS.length + 1}
        canGoBack={setupStepIndex > 0}
        onBack={goToPrevSetupStep}
        onContinue={isLastStep ? acquireCameraAndEnterPositioning : goToNextSetupStep}
        continueLoading={isLastStep && starting}
        error={error}
        permissionDenied={permissionDenied}
      />
    );
  }

  if (phase === PHASES.POSITIONING) {
    return (
      <PositioningSlide
        candidateName={interviewData?.candidate_name}
        jobRole={interviewData?.job_role}
        videoRefCallback={videoRefCallback}
        cameraArmed={cameraArmed}
        connecting={starting}
        onRetryCamera={armPositioningCamera}
        onSwitchCamera={rotateCameraFacing}
        showBack={positioningBackEnabled}
        onBack={backFromPositioning}
        onConfirm={confirmPositioning}
        confirmLoading={starting}
        error={error}
        permissionDenied={permissionDenied}
        progressCurrent={SETUP_STEPS.length}
        progressTotal={SETUP_STEPS.length + 1}
        isLandscape={isLandscape}
      />
    );
  }

  // phase === "active": monitoring is live
  return (
    <ActiveScreen
      candidateName={interviewData?.candidate_name}
      jobRole={interviewData?.job_role}
      isFullscreen={isFullscreen}
      fullscreenSupported={fullscreenSupported}
      connectionLost={connectionLost}
      videoRefCallback={videoRefCallback}
      onRotate={rotateCameraFacing}
      onToggleFullscreen={toggleFullscreen}
      isLandscape={isLandscape}
    />
  );
};

export default SecondaryCamera;
